import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useSettings } from "../lib/settings";
import { Section, Heading, PlantBadge, Spinner } from "../components/Bits";
import NewsletterForm from "../components/NewsletterForm";
import { WhatWeDontDo } from "../components/TourBits";
import {
  ArrowRight,
  Camera,
  Sparkles,
  ShieldAlert,
  Mail,
  Wind,
  Calendar as CalIcon,
  Clock,
  MapPin,
  Brain,
  Flame,
  Scale,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const HERO_IMG_FALLBACK = "/fotos/sonnenwald.webp";
const HERO_ALT = "Hohe Bäume mit Sonnenstern – Wald in Luxemburg";

const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** Build a verdict for foraging conditions from the weather payload. */
function foragingVerdict(w) {
  if (!w?.current) return null;
  const label = (w.current.label || "").toLowerCase();
  const temp = w.current.temp;
  const wind = w.current.wind;
  const wet = /(regen|schauer|gewitter|sturm)/.test(label);
  const windy = wind > 25;
  const cold = temp < 5;
  const hot = temp > 30;

  if (wet) return { tone: "warn", icon: AlertCircle, text: "Lieber warten – nasses Laub verwischt Bestimmungs-Details." };
  if (windy) return { tone: "warn", icon: AlertCircle, text: `Zu windig (${Math.round(wind)} km/h) – Blätter wackeln, Fotos werden unscharf.` };
  if (cold) return { tone: "warn", icon: AlertCircle, text: "Zu kalt – kaum Pflanzen aktiv." };
  if (hot) return { tone: "info", icon: AlertCircle, text: "Hitze – früh am Morgen sammeln ist angenehmer." };
  return { tone: "good", icon: CheckCircle2, text: "Gute Sammelbedingungen – trocken, mild und wenig Wind." };
}

export default function Home() {
  const s = useSettings();
  const [potw, setPotw] = useState(null);
  const [count, setCount] = useState(null);
  const [weather, setWeather] = useState(null);
  const [inSeason, setInSeason] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/plants/of-the-week").catch(() => null),
      api.get("/plants/count").catch(() => ({ data: { count: 0 } })),
      api.get("/weather").catch(() => null),
      api.get("/plants/in-season?limit=6").catch(() => ({ data: { plants: [] } })),
      api.get("/tours/dates?limit=3").catch(() => ({ data: [] })),
    ])
      .then(([potwRes, countRes, weatherRes, seasonRes, toursRes]) => {
        setPotw(potwRes?.data || null);
        setCount(countRes?.data?.count ?? 0);
        setWeather(weatherRes?.data || null);
        setInSeason(seasonRes?.data?.plants || []);
        setTours((toursRes?.data || []).slice(0, 3));
      })
      .finally(() => setLoading(false));
  }, []);

  const monthLabel = MONTHS_DE[new Date().getMonth()];
  const verdict = useMemo(() => foragingVerdict(weather), [weather]);

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section className="relative w-full" data-testid="hero-section">
        <div className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
          <img
            src={s.hero_image_url || HERO_IMG_FALLBACK}
            alt={(s.hero_image_url || HERO_IMG_FALLBACK).startsWith("/fotos/sonnenwald") ? HERO_ALT : "Wald in Luxemburg"}
            className="absolute inset-0 w-full h-full object-cover"
            fetchpriority="high"
            data-testid="hero-image"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(26,47,28,0.85) 0%, rgba(26,47,28,0.45) 45%, rgba(26,47,28,0.15) 100%)",
            }}
          />
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12 flex items-end pb-20 md:pb-28">
            <div className="max-w-2xl">
              <div className="overline mb-4" style={{ color: "#E8D7B5" }}>{s.hero_overline}</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--wp-cream)] leading-[1.05] tracking-tight whitespace-pre-line">
                {s.hero_title}
              </h1>
              <p className="mt-6 text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "rgba(245,240,232,0.92)" }}>
                {s.hero_subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/tours" className="btn-primary" data-testid="hero-cta-tours">
                  Tour buchen <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/identify" className="btn-secondary" style={{ borderColor: "#F5F0E8", color: "#F5F0E8" }} data-testid="hero-cta-identify">
                  <Camera className="w-4 h-4" /> Pflanze erkennen
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- JETZT IM [MONAT] ---------------- */}
      <Section className="!py-12 md:!py-16" data-testid="section-jetzt-im-monat">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
          <Heading
            overline="Jetzt im Jahr"
            title={`Jetzt im ${monthLabel}`}
            subtitle={count ? `${count} Wildpflanzen in unserer Datenbank – diese kannst du gerade entdecken.` : "Was du gerade draußen entdecken kannst."}
          />
          <Link to="/calendar" className="btn-secondary text-sm" data-testid="jetzt-im-monat-cal">
            Saisonkalender <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Weather verdict line */}
        {weather?.current && verdict && (
          <div
            className="plant-card p-4 md:p-5 mb-6 flex items-center gap-4 flex-wrap"
            data-testid="weather-verdict"
            style={{
              backgroundColor:
                verdict.tone === "good" ? "rgba(74,93,76,0.08)" :
                verdict.tone === "warn" ? "rgba(212,160,23,0.10)" :
                "var(--wp-cream)",
            }}
          >
            <div className="flex items-center gap-3">
              <div className="text-4xl leading-none">{weather.current.icon}</div>
              <div>
                <div className="font-display text-2xl leading-none">{Math.round(weather.current.temp)}°</div>
                <div className="text-xs opacity-70 mt-0.5 flex items-center gap-1">
                  {weather.current.label} · <Wind className="w-3 h-3 inline" /> {Math.round(weather.current.wind)} km/h
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm md:text-base flex-1 min-w-[220px]"
              style={{ color: verdict.tone === "good" ? "var(--wp-olive)" : verdict.tone === "warn" ? "#8B5A2B" : "var(--wp-text)" }}>
              <verdict.icon className="w-5 h-5 shrink-0" />
              <span>{verdict.text}</span>
            </div>
          </div>
        )}

        {/* Horizontal plant card strip */}
        {loading ? (
          <Spinner />
        ) : inSeason.length === 0 ? (
          <div className="text-sm opacity-60 italic">Gerade keine Pflanzen für diesen Monat verfügbar.</div>
        ) : (
          <div
            className="flex gap-4 overflow-x-auto pb-3 -mx-6 px-6 md:mx-0 md:px-0 snap-x snap-mandatory scrollbar-thin"
            data-testid="in-season-strip"
            style={{ scrollbarColor: "var(--wp-olive) transparent" }}
          >
            {inSeason.map((p) => (
              <Link
                key={p.id}
                to={`/plants/${p.slug}`}
                className="plant-card group block shrink-0 w-[260px] md:w-[280px] snap-start"
                data-testid={`in-season-card-${p.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.season_image_url || p.image_url} alt={p.image_alt && (p.season_image_url || p.image_url) === p.image_url && (p.image_url || "").startsWith("/fotos/") ? p.image_alt : p.name_de} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <h3 className="font-display text-lg truncate">{p.name_de}</h3>
                    <PlantBadge category={p.category} />
                  </div>
                  <div className="italic text-[11px] opacity-70">{p.name_lat}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* ---------------- PFLANZE DER WOCHE ---------------- */}
      {potw && (
        <Section className="!pt-0">
          <div className="grid lg:grid-cols-2 gap-10 items-center plant-card overflow-hidden" data-testid="potw-block">
            <div className="aspect-[4/3] lg:aspect-auto lg:h-full relative">
              <img src={potw.image_url} alt={potw.image_alt && (potw.image_url || "").startsWith("/fotos/") ? potw.image_alt : potw.name_de} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-12">
              <div className="overline mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Pflanze der Woche
              </div>
              <h3 className="font-display text-3xl md:text-4xl">{potw.name_de}</h3>
              <div className="italic text-sm opacity-70 mt-1">{potw.name_lat}</div>
              <div className="mt-4"><PlantBadge category={potw.category} /></div>
              <p className="mt-5 text-base leading-relaxed" style={{ color: "var(--wp-text-muted)" }}>
                {potw.short_description}
              </p>
              <Link to={`/plants/${potw.slug}`} className="btn-secondary mt-6 inline-flex" data-testid="potw-detail-link">
                Mehr erfahren <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* ---------------- SICHER SAMMELN ---------------- */}
      <Section className="!pt-0">
        <div
          className="rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start"
          style={{ backgroundColor: "rgba(167,58,58,0.08)", border: "1px solid rgba(167,58,58,0.2)" }}
          data-testid="safety-block"
        >
          <ShieldAlert className="w-10 h-10 shrink-0" style={{ color: "var(--wp-toxic)" }} />
          <div>
            <h3 className="font-display text-2xl">{s.safety_title}</h3>
            <p className="mt-3 text-sm md:text-base leading-relaxed" style={{ color: "var(--wp-text-muted)" }}>
              {s.safety_text}
            </p>
          </div>
        </div>
      </Section>

      {/* ---------------- TOUREN (konkrete Termine) ---------------- */}
      <Section className="!pt-0">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <Heading
            overline="Geführte Touren"
            title={s.tours_title || "Komm mit uns raus."}
            subtitle={s.tours_text}
          />
          <Link to="/tours" className="btn-secondary text-sm" data-testid="home-tours-all">
            Alle Touren <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="rounded-2xl overflow-hidden mb-6 aspect-[16/7] md:aspect-[21/8]">
          <img src="/fotos/guide-see.webp" alt="Michèle erklärt am Ufer eine Pflanze" loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }} data-testid="home-tours-image" />
        </div>
        {tours.length === 0 ? (
          <div className="plant-card p-6 text-sm opacity-70 italic">
            Zurzeit keine Termine veröffentlicht. Trag dich in den Newsletter ein – wir melden uns, sobald die nächste Tour steht.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="home-tours-list">
            {tours.map((t) => {
              const d = t.date ? new Date(t.date) : null;
              const dateLine = d
                ? d.toLocaleDateString("de-LU", { weekday: "long", day: "numeric", month: "long" })
                : "Termin folgt";
              const timeLine = d ? d.toLocaleTimeString("de-LU", { hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <Link
                  key={t.id}
                  to={`/tours/${t.tour_slug}`}
                  className="plant-card group block overflow-hidden"
                  data-testid={`home-tour-${t.id}`}
                >
                  {t.image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={t.image_url} alt={t.image_alt || t.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="overline text-[10px] mb-2 flex items-center gap-1.5">
                      <CalIcon className="w-3 h-3" /> {dateLine}
                    </div>
                    <h3 className="font-display text-xl leading-tight mb-2">{t.title}</h3>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-75">
                      {timeLine && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {timeLine}</span>}
                      {t.meeting_point && <span className="flex items-center gap-1 truncate max-w-[180px]"><MapPin className="w-3 h-3" /> {t.meeting_point}</span>}
                      <span className="font-medium" style={{ color: "var(--wp-primary)" }}>Kostenlos</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Section>

      {/* ---------------- WAS WIR NICHT TUN ---------------- */}
      <Section className="!pt-0">
        <WhatWeDontDo testId="home-what-we-dont-do" />
      </Section>

      {/* ---------------- WERKZEUGE (2x2) ---------------- */}
      <Section className="!pt-0">
        <Heading overline="Werkzeuge" title="Deine Begleiter" subtitle="Kleine Helfer für die Praxis – vom KI-Coach bis zum Vergleich." />
        <div className="grid grid-cols-2 gap-4 md:gap-6 mt-8" data-testid="werkzeuge-grid">
          {[
            { to: "/coach", icon: Brain, label: "KI-Coach", desc: "Frag unseren KI-Coach zu Pflanzen, Rezepten und Erkennung." },
            { to: "/recipes", icon: Flame, label: "Rezept-Generator", desc: "KI-Rezepte aus deinen Fundstücken." },
            { to: "/compare", icon: Scale, label: "Vergleich", desc: "Essbar oder giftig? Lookalikes nebeneinander." },
            { to: "/quiz", icon: HelpCircle, label: "Quiz", desc: "Teste dein Wildpflanzen-Wissen." },
          ].map((t, i) => (
            <Link
              key={t.to}
              to={t.to}
              className="plant-card p-5 md:p-6 flex flex-col gap-2 hover:border-[var(--wp-primary)]"
              data-testid={`werkzeug-${i}`}
            >
              <t.icon className="w-7 h-7" style={{ color: "var(--wp-primary)" }} />
              <div className="font-display text-xl mt-1">{t.label}</div>
              <div className="text-xs md:text-sm opacity-70">{t.desc}</div>
            </Link>
          ))}
        </div>
      </Section>

      {/* ---------------- NEWSLETTER ---------------- */}
      <Section className="!pt-0 !pb-16">
        <div className="relative overflow-hidden rounded-2xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center" style={{ backgroundColor: "#1C2E1F", color: "var(--wp-cream)" }} data-testid="newsletter-block">
          <img src="/fotos/kraeuter-hand.webp" alt="Frisch gesammelte Wildkräuter in der Hand" loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid="newsletter-bg-image" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(28,46,31,0.92) 0%, rgba(28,46,31,0.78) 55%, rgba(28,46,31,0.6) 100%)" }} />
          <div className="relative">
            <div className="overline mb-3" style={{ color: "#D4A373" }}>Newsletter</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight text-[var(--wp-cream)]">{s.newsletter_title}</h2>
            <p className="mt-4 leading-relaxed opacity-90">{s.newsletter_text}</p>
            <div className="flex items-center gap-2 text-xs mt-3 opacity-70">
              <Mail className="w-3.5 h-3.5" /> Versand: Montags 09:00 Uhr
            </div>
          </div>
          <div className="relative">
            <NewsletterForm variant="footer" />
          </div>
        </div>
      </Section>
    </div>
  );
}
